import type { CSSProperties } from "react";

/** Key left / pie right — top-aligned; pie sits in the right half at ~60% of that column. */
const SIDE_BY_SIDE_GRID: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  alignItems: "start",
  columnGap: 12,
  width: "100%",
};

const PIE_HALF: CSSProperties = {
  minWidth: 0,
  boxSizing: "border-box",
  padding: 8,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
};

const PIE_SVG_STYLE: CSSProperties = {
  display: "block",
  width: "60%",
  height: "auto",
  aspectRatio: "1",
};

interface PieSlice {
  label: string;
  value: number;
  color: string;
  /** When set and the wedge is large enough, drawn on the slice (use a short name). */
  pieLabel?: string;
}

interface PieChartProps {
  slices: PieSlice[];
  formatValue?: (value: number) => string;
  /** Legend under the pie (fits narrow dashboard columns). */
  stackedLegend?: boolean;
  /** Smaller pie + legend for three-across half-width panels. */
  mini?: boolean;
  /** Native SVG tooltip on each slice path. */
  sliceTooltip?: (slice: PieSlice, index: number) => string;
  /**
   * Click: open in place. Middle-click or Ctrl/Cmd+click (see `e`): open in a new tab — see
   * `packages/analytics-app/src/lib/dashboardNav.ts`.
   */
  onSliceClick?: (
    slice: PieSlice,
    index: number,
    e?: Pick<MouseEvent, "button" | "ctrlKey" | "metaKey">,
  ) => void;
}

function fmtPct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

/**
 * Pie size knobs — change these defaults to make pies bigger fleet-wide:
 * - Default (side-by-side: key left, pie right): pie fills the right **50%** of the card
 *   (with padding). CSS `w-1/2` + `aspect-square w-full` — no fixed px. Bump viewBox/`r`
 *   only if the wedge needs more internal inset; for on-screen size, change the 50% column.
 * - `stackedLegend`: pie above key — mid size; bump `r` / `height` in that branch.
 * - `stackedLegend` + `mini`: tightest (three-across half-width); bump `r` / `width` there.
 */
function pieLayout(stackedLegend: boolean, mini: boolean) {
  if (stackedLegend && mini) {
    // Match ~1/3 column width so SVG labels are not shrunk; height follows aspect ratio.
    return {
      width: 140,
      height: 128,
      cx: 70,
      cy: 64,
      r: 54,
      heightAuto: true,
      legendClass: "text-[11px]",
    };
  }
  if (stackedLegend) {
    return {
      width: 280,
      height: 160,
      cx: 140,
      cy: 72,
      r: 56,
      svgHeight: 120,
      heightAuto: false,
      legendClass: "text-[11px]",
    };
  }
  // Side-by-side viewBox (unit square); CSS sizes the SVG to half the card width.
  return {
    width: 400,
    height: 400,
    cx: 200,
    cy: 200,
    r: 188,
    heightAuto: false,
    legendClass: "text-[13px]",
  };
}

type PiePath =
  | { kind: "circle"; slice: PieSlice; index: number; tip: string; showPieLabel: boolean }
  | {
      kind: "wedge";
      slice: PieSlice;
      index: number;
      d: string;
      tip: string;
      showPieLabel: boolean;
      labelX: number;
      labelY: number;
    };

function buildPiePaths(
  slices: PieSlice[],
  total: number,
  cx: number,
  cy: number,
  r: number,
  sliceTooltip: PieChartProps["sliceTooltip"],
  onSliceClick: PieChartProps["onSliceClick"],
  formatValue: PieChartProps["formatValue"],
): PiePath[] {
  const positive = slices
    .map((slice, index) => ({ slice, index }))
    .filter(({ slice }) => slice.value > 0);

  if (positive.length === 1) {
    const { slice, index } = positive[0]!;
    const tip =
      sliceTooltip?.(slice, index) ??
      (onSliceClick
        ? `${slice.label}: ${formatValue ? formatValue(slice.value) : slice.value} — Click: open Raw Events (filtered). Middle-click / Ctrl/Cmd+click: new tab.`
        : `${slice.label}: ${formatValue ? formatValue(slice.value) : slice.value}`);
    return [{ kind: "circle", slice, index, tip, showPieLabel: Boolean(slice.pieLabel) }];
  }

  let angle = -Math.PI / 2;
  const paths: PiePath[] = [];
  for (const { slice, index } of positive) {
    const sliceAngle = (slice.value / total) * Math.PI * 2;
    const midAngle = angle + sliceAngle / 2;
    const x1 = cx + r * Math.cos(angle);
    const y1 = cy + r * Math.sin(angle);
    const x2 = cx + r * Math.cos(angle + sliceAngle);
    const y2 = cy + r * Math.sin(angle + sliceAngle);
    const largeArc = sliceAngle > Math.PI ? 1 : 0;
    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    const labelR = r * 0.58;
    const labelX = cx + labelR * Math.cos(midAngle);
    const labelY = cy + labelR * Math.sin(midAngle);
    angle += sliceAngle;
    const tip =
      sliceTooltip?.(slice, index) ??
      (onSliceClick
        ? `${slice.label}: ${formatValue ? formatValue(slice.value) : slice.value} — Click: open Raw Events (filtered). Middle-click / Ctrl/Cmd+click: new tab.`
        : `${slice.label}: ${formatValue ? formatValue(slice.value) : slice.value}`);
    const showPieLabel = Boolean(slice.pieLabel) && sliceAngle >= 0.28;
    paths.push({ kind: "wedge", slice, index, d, tip, showPieLabel, labelX, labelY });
  }
  return paths;
}

function renderSliceShape(
  p: PiePath,
  cx: number,
  cy: number,
  r: number,
  onSliceClick: PieChartProps["onSliceClick"],
) {
  const pointerProps = {
    style: { cursor: onSliceClick ? "pointer" : undefined } as const,
    onClick: onSliceClick
      ? (e: { button: number; ctrlKey: boolean; metaKey: boolean }) =>
          onSliceClick(p.slice, p.index, e)
      : undefined,
    onAuxClick: onSliceClick
      ? (e: { button: number; ctrlKey: boolean; metaKey: boolean }) => {
          if (e.button !== 1) return;
          onSliceClick(p.slice, p.index, e);
        }
      : undefined,
  };

  if (p.kind === "circle") {
    return (
      <circle key={`slice-${p.index}`} cx={cx} cy={cy} r={r} fill={p.slice.color} {...pointerProps}>
        <title>{p.tip}</title>
      </circle>
    );
  }

  return (
    <path key={`slice-${p.index}`} d={p.d} fill={p.slice.color} {...pointerProps}>
      <title>{p.tip}</title>
    </path>
  );
}

function PieLegend({
  slices,
  total,
  formatValue,
  legendClass,
}: {
  slices: PieSlice[];
  total: number;
  formatValue?: (value: number) => string;
  legendClass: string;
}) {
  return (
    <ul className={`min-w-0 space-y-0.5 text-text-secondary ${legendClass}`}>
      {slices.map((s, i) => (
        <li key={i} className="flex items-start gap-1">
          <span
            className="mt-0.5 inline-block h-2 w-2 shrink-0 rounded-sm"
            style={{ backgroundColor: s.color }}
            aria-hidden
          />
          <span className="min-w-0 leading-tight text-text-primary">
            {s.label}: {formatValue ? formatValue(s.value) : s.value} ({fmtPct(s.value / total)})
          </span>
        </li>
      ))}
    </ul>
  );
}

function PieSvg({
  width,
  height,
  cx,
  cy,
  r,
  paths,
  onSliceClick,
  /** Fill parent width as a square (side-by-side half-column). */
  fillWidthSquare,
  svgHeight,
  heightAuto,
  pieLabelFontSize,
}: {
  width: number;
  height: number;
  cx: number;
  cy: number;
  r: number;
  paths: PiePath[];
  onSliceClick: PieChartProps["onSliceClick"];
  fillWidthSquare?: boolean;
  svgHeight?: number;
  heightAuto: boolean;
  pieLabelFontSize: number;
}) {
  const labelStroke = fillWidthSquare ? 5 : 2.5;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={fillWidthSquare ? undefined : heightAuto ? undefined : svgHeight}
      className={fillWidthSquare ? undefined : "block"}
      style={fillWidthSquare ? PIE_SVG_STYLE : undefined}
      preserveAspectRatio="xMidYMid meet"
    >
      {paths.map((p) => renderSliceShape(p, cx, cy, r, onSliceClick))}
      {paths.map((p) =>
        p.kind === "wedge" && p.showPieLabel ? (
          <text
            key={`lbl-${p.index}`}
            x={p.labelX}
            y={p.labelY}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#f4f7ff"
            fontSize={pieLabelFontSize}
            fontWeight="600"
            stroke="#1a2b46"
            strokeWidth={labelStroke}
            paintOrder="stroke"
            pointerEvents="none"
          >
            {p.slice.pieLabel}
          </text>
        ) : null,
      )}
    </svg>
  );
}

export function PieChart({
  slices,
  formatValue,
  stackedLegend = false,
  mini = false,
  sliceTooltip,
  onSliceClick,
}: PieChartProps) {
  const layout = pieLayout(stackedLegend, mini);
  const { width, height, cx, cy, r, heightAuto, legendClass } = layout;
  const svgHeight = "svgHeight" in layout ? layout.svgHeight : undefined;

  const total = slices.reduce((a, b) => a + b.value, 0);

  if (total <= 0) {
    if (stackedLegend) {
      return (
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height={heightAuto ? undefined : svgHeight}
          className={heightAuto ? "block" : undefined}
          preserveAspectRatio="xMidYMid meet"
        >
          <circle cx={cx} cy={cy} r={r} fill="#1a2b46" />
          <text x={cx} y={cy + 4} textAnchor="middle" fill="#9eb2d7" fontSize={11}>
            No data
          </text>
        </svg>
      );
    }
    return (
      <div style={SIDE_BY_SIDE_GRID}>
        <p className={`${legendClass} text-text-secondary`} style={{ minWidth: 0 }}>
          No data
        </p>
        <div style={PIE_HALF}>
          <svg
            viewBox={`0 0 ${width} ${height}`}
            style={PIE_SVG_STYLE}
            preserveAspectRatio="xMidYMid meet"
          >
            <circle cx={cx} cy={cy} r={r} fill="#1a2b46" />
            <text x={cx} y={cy + 4} textAnchor="middle" fill="#9eb2d7" fontSize={22}>
              No data
            </text>
          </svg>
        </div>
      </div>
    );
  }

  const paths = buildPiePaths(slices, total, cx, cy, r, sliceTooltip, onSliceClick, formatValue);

  if (stackedLegend) {
    return (
      <div className="flex min-w-0 flex-col items-center gap-1">
        <PieSvg
          width={width}
          height={height}
          cx={cx}
          cy={cy}
          r={r}
          paths={paths}
          onSliceClick={onSliceClick}
          svgHeight={svgHeight}
          heightAuto={heightAuto}
          pieLabelFontSize={mini ? 9 : 10}
        />
        <div className="w-full min-w-0">
          <PieLegend
            slices={slices}
            total={total}
            formatValue={formatValue}
            legendClass={legendClass}
          />
        </div>
      </div>
    );
  }

  // Key top-left 50% / pie right 50% (inline grid so layout cannot depend on Tailwind purge).
  return (
    <div style={SIDE_BY_SIDE_GRID}>
      <div style={{ minWidth: 0 }}>
        <PieLegend
          slices={slices}
          total={total}
          formatValue={formatValue}
          legendClass={legendClass}
        />
      </div>
      <div style={PIE_HALF}>
        <PieSvg
          width={width}
          height={height}
          cx={cx}
          cy={cy}
          r={r}
          paths={paths}
          onSliceClick={onSliceClick}
          fillWidthSquare
          heightAuto={false}
          pieLabelFontSize={22}
        />
      </div>
    </div>
  );
}
